import {Box, Button, CircularProgress, Divider, IconButton, Typography, useTheme} from "@mui/material";
import axios from "axios";
import {useEffect, useState} from "react";
import {FiEdit2, FiTrash2} from "react-icons/fi";
import {useLocation, useNavigate} from "react-router-dom";
import UserForm from "../components/Users/UserForm";
import UserInfo from "../components/Users/UserInfo";
import UserRolesGrid from "../components/Users/UserRolesGrid";
import Window from "../components/Window/Window";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import useApiRequest from "../hooks/useApiRequest";
import useConfirmDialog from "../hooks/useConfirmDialog";
import UserTokensGrid from "../components/Users/UserTokensGrid";

const AUTH_URL = process.env.REACT_APP_APIAUTH_BASE_URL;

const axiosInstance = axios.create({
  baseURL: AUTH_URL
});

const UserDetail = () => {
  const {state} = useLocation();
  const navigate = useNavigate();
  const id = state?.id;

  const isNewUser = !id;

  const {data, loading, read, remove} = useApiRequest({baseUrl: `/users`, resource: "users"});

  const [isEditing, setIsEditing] = useState(isNewUser);

  const theme = useTheme();

  // Hook para el diálogo de confirmación de borrado
  const {confirmOpen, itemName, openDialog, handleConfirm, handleCancel} = useConfirmDialog(async () => {
    try {
      await remove(id);
      navigate("/users");
    } catch (error) {
      console.error("Error al eliminar el usuario:", error);
    }
  });

  useEffect(() => {
    if (!isNewUser) {
      (async () => {
        try {
          await read(id);
        } catch (error) {
          console.error("Error al cargar los detalles del usuario:", error);
        }
      })();
    }
    // eslint-disable-next-line
  }, [id]);

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleOpenDeleteDialog = () => {
    openDialog(id, data?.name || "");
  };

  const handleResetPassword = async () => {
    const confirmReset = window.confirm(
      "¿Estás seguro de que deseas enviar un correo de cambio de contraseña a este usuario?"
    );
    if (confirmReset) {
      const url = `${AUTH_URL}/auth/reset-password`;

      await axiosInstance
        .post(url, {email: data.email})
        .then((response) => {
          console.log("Correo de reinicio de contraseña enviado con éxito");
        })
        .catch((error) => {
          console.error("Error al enviar el correo:", error);
        });
    }
  };

  return (
    <>
      <Window
        title={isNewUser ? "Nuevo Usuario" : "Detalles del Usuario"}
        topRightButtons={
          <>
            {!isNewUser && !isEditing && (
              <Button variant="contained" color="primary" onClick={handleResetPassword}>
                Reiniciar Contraseña
              </Button>
            )}

            {!isNewUser && (
              <Box component="span">
                {isEditing ? (
                  <Button variant="contained" color="primary" onClick={handleEditToggle}>
                    Cancelar
                  </Button>
                ) : (
                  <IconButton aria-label="edit" color="primary" onClick={handleEditToggle}>
                    <FiEdit2 />
                  </IconButton>
                )}

                <IconButton aria-label="delete" color="error" onClick={handleOpenDeleteDialog}>
                  <FiTrash2 />
                </IconButton>
              </Box>
            )}
          </>
        }
      >
        {loading && !isNewUser ? (
          <CircularProgress />
        ) : (
          <>
            <Box sx={{mb: 4}}>
              {isEditing ? (
                <UserForm
                  isNew={isNewUser}
                  refreshData={async () => {
                    try {
                      await read(id);
                    } catch (error) {
                      console.error("Error al cargar los detalles del usuario:", error);
                    }
                  }}
                  setIsEditing={setIsEditing}
                  initialUserData={data}
                />
              ) : (
                <UserInfo user={data} />
              )}
            </Box>

            {!isNewUser && (
              <>
                <Typography variant="h3" fontWeight="bold" sx={{color: theme.palette.text.title, mt: 4, mb: 2}}>
                  Roles
                </Typography>
                <Divider sx={{mb: 2, borderColor: theme.palette.text.divider}} />
                <UserRolesGrid userId={id} />
              </>
            )}

            {!isNewUser && (
              <>
                <Typography variant="h3" fontWeight="bold" sx={{color: theme.palette.text.title, mt: 4, mb: 2}}>
                  Tokens
                </Typography>
                <Divider sx={{mb: 2, borderColor: theme.palette.text.divider}} />
                <UserTokensGrid userId={id} />
              </>
            )}
          </>
        )}
      </Window>

      <ConfirmDialog
        open={confirmOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        entityName="usuario"
        entityLabel={itemName}
      />
    </>
  );
};

export default UserDetail;
